/**
 * Detalhe do caso de garantia/assistência (Task 15, brief 3.6).
 *
 * Reúne, para o MESMO caso, a prova (nº de série + comprovante), a resolução, o
 * responsável/prazo da etapa atual e a linha do tempo auditável com cada etapa,
 * responsável, prazo, evidências e comunicação (D-15.1/D-15.2).
 *
 * Regras que a tela respeita:
 * - **pedido/cliente navegáveis**: o caso referencia o pedido unificado e o
 *   cliente canônico; não é uma segunda cópia da venda;
 * - **nada inventado**: campo ainda não informado aparece como "não informado"
 *   com o motivo, não como vazio;
 * - **a máquina decide o que é possível**: os próximos passos vêm da regra
 *   pura, não de uma lista de botões desta tela;
 * - **o RMA não paga**: a resolução reembolso/troca mostra a referência gerada
 *   para o Financeiro (Task 16), sem executar o pagamento.
 *
 * Migrado para `.tsx`/JSX real (Cluster D — Logística & Garantias, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * (`createElement` → JSX) + um ícone lucide por bloco (`SectionTitle`), no
 * mesmo padrão visual de `ExpedicaoDetalhe.tsx`/`PedidoDetalhe.tsx`/
 * `KpiCard.tsx` (ícone em caixa colorida ao lado do título). Esta tela não
 * tinha NENHUM ícone antes da migração (lacuna real, achado da investigação
 * inicial do cluster) — os quatro ícones abaixo são puramente decorativos
 * (aria-hidden); o nome acessível de cada bloco continua sendo o texto do
 * `h3`. Nenhum `data-*`/aria/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `FulfillmentStateMachine.tsx`.
import React, { type ReactElement, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "../ui/index.js";
import { ArrowLeft, Clock, ClipboardList, Film, Image as ImageIcon, MessageSquareQuote, FileCheck2, Receipt, type LucideIcon } from "lucide-react";
import { RmaBoundary } from "../components/RmaBoundary.js";
import { RmaStateMachine } from "../components/RmaStateMachine.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { PendingField } from "../components/PendingField.js";
import { isRmaSourceAwaitingContract, orderDrillDown, rmaCaseById } from "../rma/dataset.js";
import { RMA_STATE_LABELS, RMA_RESOLUTION_LABELS } from "../rma/state-machine.js";
import { useRmaTransition } from "../queries.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatDateTime } from "../format.js";
import type { RmaDataset } from "../rma/dataset.js";
import type { RmaDetalheViewProps, RmaTransitionHandle } from "../rma/view-props.js";
import type { PlatformRmaCase } from "../api/types.js";

/** Valor ausente nunca vira vazio mudo: a tela diz por que não há valor (ícone+texto). */
function pending(label: string): ReactElement {
  return <PendingField label={label} />;
}

/** Casca de seção no padrão aprovado: cartão tokenizado + título h3 (= PedidoDetalhe). */
const BLOCK_CLASS = "rounded-panel border border-border bg-surface p-4 shadow-s1 flex flex-col gap-3";

/**
 * Título de bloco com ícone em caixa colorida — mesmo padrão visual do
 * `kpi-card__icon` em `KpiCard.tsx` (ícone decorativo, o nome acessível do
 * bloco continua sendo o texto do `h3`).
 */
function SectionTitle({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand-wash text-brand-text">
        <Icon className="h-4 w-4" aria-hidden focusable={false} />
      </span>
      <h3 className="text-lg font-extrabold text-ink">{children}</h3>
    </div>
  );
}

function CaseBlock({ rmaCase }: { rmaCase: PlatformRmaCase }): ReactElement {
  const drill = orderDrillDown(rmaCase);
  return (
    <section className={`rma-detail__case ${BLOCK_CLASS}`} data-section="caso">
      <SectionTitle icon={ClipboardList}>Caso</SectionTitle>
      <p data-protocol-code={rmaCase.protocolCode ?? ""}>
        {"Protocolo: "}
        {rmaCase.protocolCode ?? pending("lavrado na etapa de protocolo")}
      </p>
      <p data-defect-summary={rmaCase.defectSummary}>
        {"Defeito relatado: "}
        {rmaCase.defectSummary || pending("sem relato")}
      </p>
      <p data-customer-id={rmaCase.customerId}>
        {"Cliente: "}
        <Link to={`/clientes/${rmaCase.customerId}`}>{rmaCase.customerName ?? rmaCase.customerId}</Link>
      </p>
      {drill ? (
        <p data-order-link={drill.route}>
          {"Pedido: "}
          <Link to={drill.route}>{drill.reference}</Link>
        </p>
      ) : (
        <p>
          {"Pedido: "}
          {pending("caso ainda não vinculado a um pedido")}
        </p>
      )}
      <p data-product-id={rmaCase.productId ?? ""}>
        {"Produto: "}
        {rmaCase.productName ?? rmaCase.productId ?? pending("produto não identificado")}
      </p>
    </section>
  );
}

type EvidenceKind = "image" | "video" | "file";

function evidenceKind(url: string): EvidenceKind {
  const path = url.split("?")[0].toLowerCase();
  if (/\.(png|jpe?g|webp|gif|svg|avif)$/.test(path)) return "image";
  if (/\.(mp4|webm|mov|m4v)$/.test(path)) return "video";
  return "file";
}

/**
 * Relato do cliente + evidências (fotos, vídeo, laudo) reunidos dos passos do
 * caso. A URL vem do storage (simulada em dev); o tipo é inferido pela
 * extensão. Cada item diz em qual etapa foi anexado, para o controle ficar
 * auditável. Sem evidência, a tela diz isso em vez de omitir o bloco.
 */
function EvidenceBlock({ rmaCase }: { rmaCase: PlatformRmaCase }): ReactElement {
  const items = rmaCase.events.flatMap((event) =>
    event.evidenceUrls.map((url) => ({ url, stage: RMA_STATE_LABELS[event.toState], kind: evidenceKind(url) }))
  );
  return (
    <section className={`rma-detail__evidence ${BLOCK_CLASS} lg:col-span-2`} data-section="evidencias" data-evidence-count={items.length}>
      <SectionTitle icon={MessageSquareQuote}>Relato do cliente e evidências</SectionTitle>
      <blockquote className="m-0 rounded-control border-l-4 border-brand bg-surface-soft px-4 py-3 text-ink" data-customer-report="true">
        {rmaCase.defectSummary || pending("sem relato")}
      </blockquote>
      {items.length === 0 ? (
        <p className="m-0 text-sm text-muted">{pending("nenhuma foto, vídeo ou laudo anexado até agora")}</p>
      ) : (
        <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((item, index) => (
            <li key={`${item.url}-${index}`} className="flex flex-col gap-1" data-evidence-kind={item.kind}>
              {item.kind === "video" ? (
                <video className="aspect-video w-full rounded-control bg-black" src={item.url} controls preload="metadata" aria-label={`Vídeo anexado na etapa ${item.stage}`} />
              ) : item.kind === "image" ? (
                <a href={item.url} target="_blank" rel="noreferrer">
                  <img className="aspect-video w-full rounded-control border border-border object-cover" src={item.url} alt={`Foto anexada na etapa ${item.stage}`} loading="lazy" />
                </a>
              ) : (
                <a className="font-semibold text-brand-text hover:underline" href={item.url} target="_blank" rel="noreferrer">
                  {item.url}
                </a>
              )}
              <span className="inline-flex items-center gap-1 text-xs text-muted">
                {item.kind === "video" ? <Film className="h-3.5 w-3.5" aria-hidden focusable={false} /> : <ImageIcon className="h-3.5 w-3.5" aria-hidden focusable={false} />}
                {`${item.kind === "video" ? "Vídeo" : item.kind === "image" ? "Foto" : "Arquivo"} · etapa: ${item.stage}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Prova exigida por etapa (nº de série + comprovante) — D-15.4. */
function ProofBlock({ rmaCase }: { rmaCase: PlatformRmaCase }): ReactElement {
  return (
    <section className={`rma-detail__proof ${BLOCK_CLASS}`} data-section="prova">
      <SectionTitle icon={FileCheck2}>Prova</SectionTitle>
      <p data-serial-number={rmaCase.serialNumber ?? ""}>
        {"Número de série: "}
        {rmaCase.serialNumber ?? pending("exigido a partir do protocolo")}
      </p>
      <p data-proof-url={rmaCase.proofUrl ?? ""}>
        {"Comprovante de compra: "}
        {rmaCase.proofUrl ?? pending("exigido a partir do diagnóstico")}
      </p>
    </section>
  );
}

function ResolutionBlock({ rmaCase }: { rmaCase: PlatformRmaCase }): ReactElement {
  return (
    <section className={`rma-detail__resolution ${BLOCK_CLASS}`} data-section="resolucao">
      <SectionTitle icon={Receipt}>Resolução</SectionTitle>
      <p data-resolution={rmaCase.resolution ?? ""}>
        {"Resultado: "}
        {rmaCase.resolution ? RMA_RESOLUTION_LABELS[rmaCase.resolution] : pending("definido na etapa de resolução")}
      </p>
      <p data-financial-reference={rmaCase.financialReference ?? ""}>
        {"Referência financeira (Task 16): "}
        {rmaCase.financialReference ??
          (rmaCase.resolution === "denied" ? "não se aplica (garantia não procedente)" : pending("gerada em reembolso/troca; o RMA não paga"))}
      </p>
    </section>
  );
}

function SlaBlock({ rmaCase }: { rmaCase: PlatformRmaCase }): ReactElement {
  return (
    <section className={`rma-detail__sla ${BLOCK_CLASS}`} data-section="responsavel-prazo">
      <SectionTitle icon={Clock}>Responsável e prazo da etapa atual</SectionTitle>
      <p data-assigned-to={rmaCase.assignedTo ?? ""}>
        {"Responsável: "}
        {rmaCase.assignedTo ?? pending("etapa ainda não assumida")}
      </p>
      <p data-due-at={rmaCase.dueAt ?? ""}>
        {"Prazo: "}
        {rmaCase.dueAt ? formatDateTime(rmaCase.dueAt) : pending("sem prazo definido ainda")}
      </p>
    </section>
  );
}

export function RmaDetalheView({ cases, caseId, transition }: RmaDetalheViewProps): ReactElement {
  if (isRmaSourceAwaitingContract(cases)) {
    return (
      <div className="page page--garantias-detalhe flex flex-col gap-6" data-page="garantias-detalhe">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-extrabold text-ink">Garantias / RMA</h2>
        </header>
        <StatusPanel
          kind="awaiting_source"
          title={`Fila de garantias/RMA ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/rma/cases não respondeu neste ambiente, então não há caso para abrir."
        />
      </div>
    );
  }
  const rmaCase = rmaCaseById(cases, caseId);
  if (!rmaCase) {
    return (
      <div className="page page--garantias-detalhe flex flex-col gap-6" data-page="garantias-detalhe">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-extrabold text-ink">Garantias / RMA</h2>
        </header>
        <StatusPanel
          kind="empty"
          title={`Caso ${caseId} não está na fila`}
          detail="A fonte respondeu e não tem este identificador — dizer isso é melhor que mostrar uma tela vazia."
        />
        <Link to="/garantias" className="w-fit text-sm font-semibold text-brand-text hover:underline">
          Voltar para a fila de garantias
        </Link>
      </div>
    );
  }
  return (
    <div className="page page--garantias-detalhe flex flex-col gap-6" data-page="garantias-detalhe">
      <header className="drilldown-header flex flex-col gap-3">
        <Link to="/garantias" className="drilldown-header__back inline-flex w-fit items-center gap-1 text-sm font-bold text-muted hover:text-ink">
          <ArrowLeft className="h-4 w-4" aria-hidden focusable={false} />
          Voltar para a fila de garantias
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">{`Caso ${rmaCase.protocolCode ?? rmaCase.id}`}</h2>
          <Badge tone={rmaCase.closedAt ? "success" : "info"}>{RMA_STATE_LABELS[rmaCase.state]}</Badge>
          {rmaCase.resolution ? <Badge tone={rmaCase.resolution === "denied" ? "danger" : "neutral"}>{RMA_RESOLUTION_LABELS[rmaCase.resolution]}</Badge> : null}
          {rmaCase.demo ? <Badge tone="warning" data-demo-badge="true">Exemplo</Badge> : null}
        </div>
        <p className="drilldown-header__origin m-0 text-sm text-muted" data-record-id={rmaCase.id} data-rma-state={rmaCase.state}>
          {`Etapa: ${RMA_STATE_LABELS[rmaCase.state]} · aberto em ${formatDateTime(rmaCase.openedAt)}${
            rmaCase.closedAt ? ` · encerrado em ${formatDateTime(rmaCase.closedAt)}` : ""
          }`}
        </p>
      </header>
      {/* Blocos informativos em duas colunas; a máquina de estados (ações) ocupa a largura toda. */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        <CaseBlock rmaCase={rmaCase} />
        <ProofBlock rmaCase={rmaCase} />
        <EvidenceBlock rmaCase={rmaCase} />
        <SlaBlock rmaCase={rmaCase} />
        <ResolutionBlock rmaCase={rmaCase} />
      </div>
      <RmaStateMachine rmaCase={rmaCase} transition={transition} />
    </div>
  );
}

export function RmaDetalhe(): ReactElement {
  const params = useParams();
  const caseId = params.caseId ?? "";
  const stateMutation = useRmaTransition();
  // Mesmo padrão do detalhe de expedição (Task 14): a view recebe o caminho de
  // gravação por prop, então o teste exercita os MESMOS submits sem navegador.
  const transition: RmaTransitionHandle = {
    submit: (input) => stateMutation.mutate({ caseId, input }),
    pending: stateMutation.isPending,
    error: stateMutation.error ?? null,
    savedState: stateMutation.data?.state ?? null
  };
  return (
    <RmaBoundary>{(cases: RmaDataset) => <RmaDetalheView cases={cases} caseId={caseId} transition={transition} />}</RmaBoundary>
  );
}
